import {
  Alert,
  Button,
  CloseButton,
  Flex,
  Heading,
  IconDownloadLine,
  Modal,
  Spinner,
  Text,
} from "@instructure/ui";
import Papa from "papaparse";
import { useContext, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import "react-loading-skeleton/dist/skeleton.css";
import { CurriculumMapService } from "../../services/CurriculumMapService";
import { Files } from "../../services/FilesService";
import {
  FeatureContext,
  TermsContext,
  UserDetailContext,
} from "../../utils/Context";
import { buildCurriculumMapRows } from "../../utils/CurriculumMapExport";
import { downloadCSVTemplate } from "../../utils/DownloadCSVTemplate";
import { ERROR, useNotification } from "../CustomHooks/NotificationProvider";
import EnrichedFileDrop from "../LearningOutcomes/FileDrop/EnrichedFileDrop";
import { getEarliestCanvasTerm } from "../TermManagement/utils";
import CurriculumMapTermSelect from "./CurriculumMapTermSelect";
import { isBetaOrDev } from "../../utils/URIUtils";

const MAPPINGS_FILE_TYPE = "mappings";
const FUTURE = "future";

const headers = [
  "map_tense",
  "subject_code",
  "offering_code",
  "origin_context",
  "origin_id",
  "origin_name",
  "origin_statement",
  "destination_context",
  "destination_id",
  "destination_name",
  "destination_statement",
  "present_or_upcoming",
  "degrees_of_learning",
  "mapping_state",
];

const ManageMap = ({
  open,
  setOpen,
  rows,
  cols,
  programId,
  futureMap,
  setFutureMap,
  attainmentLevels,
  permission,
  fetchMappings,
  badgeMappings = [],
  validIssuers = [],
  validBadgeClasses = [],
}) => {
  const { t } = useTranslation();
  const { insightsTerms } = useContext(TermsContext);
  const features = useContext(FeatureContext);
  const showNotification = useNotification();
  const userDetails = useContext(UserDetailContext);
  const [mapFile, setMapFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [futureInsightsTerm, setFutureInsightsTerm] = useState("");
  const [earliestCanvasTerm, setEarliestCanvasTerm] = useState("");
  const [updatingTerm, setUpdatingTerm] = useState(false);

  const setLoSyncFlag =
    features.find((f) => f.name === "ilo_plo_sync_flag")?.value === "TRUE";

  const setBadgingFlag =
    features.find((f) => f.name === "badging_flag")?.value === "TRUE";

  useEffect(() => {
    if (setLoSyncFlag && !headers.includes("set_lo_sync")) {
      headers.push("set_lo_sync");
    }
    if (setBadgingFlag && !headers.includes("open_badge_id")) {
      headers.push("open_badge_id");
    }
    if (setBadgingFlag && !headers.includes("issuer_open_badge_id")) {
      headers.push("issuer_open_badge_id");
    }
  }, [setLoSyncFlag, setBadgingFlag]);

  useEffect(() => {
    if (futureMap?.term) {
      const insightsTerm = insightsTerms.find(
        (term) => term.id === futureMap.term.insights_term_id
      );
      setFutureInsightsTerm(insightsTerm);
      const canvasTerm = getEarliestCanvasTerm(insightsTerm);
      setEarliestCanvasTerm(canvasTerm);
    }
  }, [insightsTerms, futureMap]);

  const issuerIds = validIssuers.map((issuer) => issuer.openBadgeId || issuer);
  const badgeClassIds = validBadgeClasses.map((bc) => bc.openBadgeId || bc);

  const handleSubmit = () => {
    if (mapFile) {
      const reader = new FileReader();
      reader.onload = function (event) {
        const fileContent = event.target.result;
        let parsed;
        try {
          parsed = Papa.parse(fileContent, { skipEmptyLines: true });
        } catch (error) {
          console.error("Error parsing CSV file:", error);
          showNotification(
            ERROR,
            t(
              "Error parsing the uploaded file. Please check the file format and try again."
            )
          );
          return;
        }
        const rows = parsed.data;
        const headers = rows[0];
        const dataRows = rows.slice(1);
        let errors = false;

        const headerIndex = {};
        headers.forEach((h, idx) => {
          headerIndex[h] = idx;
        });

        for (let i = 0; i < dataRows.length; i++) {
          const columns = dataRows[i];
          const attainmentLevel = columns[headerIndex["degrees_of_learning"]];
          if (
            !!attainmentLevel &&
            !attainmentLevels.includes(attainmentLevel.toLowerCase())
          ) {
            errors = true;
            showNotification(
              t("curriculumMap.invalidAttainment", { obj: attainmentLevel }),
              ERROR
            );
            break;
          }
          const mapFlag = columns[headerIndex["mapping_state"]];
          if (mapFlag !== "mapped" && mapFlag !== "unmapped") {
            errors = true;
            showNotification(
              t("curriculumMap.invalidMapFlag", { obj: mapFlag }),
              ERROR
            );
            break;
          }
          const loSyncIdx = headerIndex["set_lo_sync"];
          const loSync =
            loSyncIdx !== undefined ? columns[loSyncIdx] : undefined;
          const originContext = columns[headerIndex["origin_context"]];
          if (
            originContext === "offering" &&
            loSyncIdx !== undefined &&
            loSync &&
            loSync.toLowerCase() !== "true" &&
            loSync.toLowerCase() !== "false"
          ) {
            errors = true;
            showNotification(
              t("curriculumMap.invalidLoSyncFlag", { obj: loSync }),
              ERROR
            );
            break;
          } else if (originContext === "CSLO" && loSyncIdx !== undefined) {
            columns[loSyncIdx] = "";
          }

          const issuerOpenBadgeId =
            columns[headerIndex["issuer_open_badge_id"]];
          const openBadgeId = columns[headerIndex["open_badge_id"]];

          if (issuerOpenBadgeId && !issuerIds.includes(issuerOpenBadgeId)) {
            errors = true;
            showNotification(
              t("curriculumMap.invalidIssuer", { issuer: issuerOpenBadgeId }),
              ERROR
            );
            break;
          }
          if (openBadgeId && !badgeClassIds.includes(openBadgeId)) {
            errors = true;
            showNotification(
              t("curriculumMap.invalidOpenBadgeClass", {
                openBadge: openBadgeId,
              }),
              ERROR
            );
            break;
          }
          dataRows[i] = columns;
        }
        if (!errors) {
          const updatedFileContent = Papa.unparse([headers, ...dataRows]);

          const updatedBlob = new Blob([updatedFileContent], {
            type: "text/csv",
          });

          const updatedFile = new File([updatedBlob], mapFile.name, {
            type: mapFile.type,
          });

          setUploading(true);
          Files.saveFile(updatedFile, MAPPINGS_FILE_TYPE)
            .then(() => {
              setOpen(false);
              setMapFile(null);
              showNotification(t("curriculumMap.uploadSuccess"));
            })
            .catch(() =>
              showNotification(t("curriculumMap.uploadError"), ERROR)
            )
            .finally(() => setUploading(false));
        }
      };
      reader.readAsText(mapFile);
    }
  };

  const handleCancel = () => {
    setOpen(false);
    setMapFile(null);
  };

  const handleFuture = () => {
    const data = buildCurriculumMapRows(rows, cols, FUTURE, {
      badgeMappings,
      setLoSyncFlag,
      futureMap,
      headers,
    });
    downloadCSVTemplate("curriculum_map.csv", headers, data);
  };

  const handleUpdateTerm = async () => {
    try {
      setUpdatingTerm(true);
      await CurriculumMapService.updateCurriculumMapTerm(
        futureMap.id,
        earliestCanvasTerm.id
      );

      await fetchMappings(true);

      setFutureMap((prevMap) => ({ ...prevMap, term: earliestCanvasTerm }));
      showNotification(
        t("notifications.general.updateSuccess", { obj: "Future Term" })
      );
    } catch {
      showNotification(
        t("notifications.general.updateError", { obj: "Future Term" }),
        ERROR
      );
    } finally {
      setUpdatingTerm(false);
    }
  };

  const handleTermSelect = (insightsTerm) => {
    setFutureInsightsTerm(insightsTerm);
    const canvasTerm = getEarliestCanvasTerm(insightsTerm);
    setEarliestCanvasTerm(canvasTerm);
  };

  return (
    <Modal open={open} label={t("global.dragFile")}>
      <Modal.Header>
        <Heading level="h2" as="h1">
          {t("curriculumMap.manageCurriculumMap")}
        </Heading>
        <CloseButton
          placement="end"
          offset="medium"
          screenReaderLabel={t("global.close")}
          onClick={handleCancel}
        />
      </Modal.Header>
      <Modal.Body>
        {!permission && (
          <Alert margin="0 0 medium 0">
            {t("manageMap.enableFutureUpload")}
          </Alert>
        )}
        {!!permission && (
          <Flex margin="0 0 small 0" gap="small" alignItems="end">
            <Flex.Item>
              <CurriculumMapTermSelect
                id="future-term-select"
                label={t("curriculumMap.term")}
                size="small"
                selectHandler={handleTermSelect}
                selectedTermId={futureInsightsTerm?.id}
                allowCurrent={isBetaOrDev(window.location.href)}
              />
            </Flex.Item>
            <Flex.Item>
              <Button
                size="small"
                color="primary"
                onClick={handleUpdateTerm}
                interaction={
                  futureMap.term?.id === earliestCanvasTerm?.id || updatingTerm
                    ? "disabled"
                    : "enabled"
                }
              >
                {updatingTerm ? (
                  <Spinner
                    size="x-small"
                    renderTitle={t("global.pleaseWait")}
                  />
                ) : (
                  t("manageMap.update")
                )}
              </Button>
            </Flex.Item>
          </Flex>
        )}
        <EnrichedFileDrop
          fileState={mapFile}
          setFileState={setMapFile}
          enriched_content={{
            user_id: userDetails.canvas_id,
            context_id: programId,
          }}
          fileDropHeading={t("manageMap.uploadMap")}
          fileExtension=".csv"
          interaction={permission && !!futureMap.term}
        />
        <div style={{ marginTop: 10 }}>
          <Text as="div">
            <Text>
              <Trans i18nKey="manageMap.futureText1">
                You may download your program&apos;s future curriculum map
                below. A data dictionary for the file contents can be downloaded
                <a href="/futureMapDataDictionary.txt" download>
                  here
                </a>
                .
              </Trans>
            </Text>
          </Text>
          <br />
          <Text as="div">
            {setLoSyncFlag ? (
              <Trans i18nKey="manageMap.futureText2_2">
                If you wish to edit your future curriculum map via file upload,
                you may download and make changes to the
                <strong>degrees_of_learning</strong>,
                <strong>mapping_state</strong>, and <strong>set_lo_sync</strong>
                as desired, then re-upload your updated file. Please do not edit
                any other column&apos;s contents.
              </Trans>
            ) : (
              <Trans i18nKey="manageMap.futureText2_1">
                If you wish to edit your future curriculum map via file upload,
                you may download and make changes to the
                <strong>degrees_of_learning</strong>,
                <strong>mapping_state</strong>, as desired, then re-upload your
                updated file. Please do not edit any other column&apos;s
                contents.
              </Trans>
            )}
          </Text>
          <br />
          <Text as="div">
            <Text>{t("manageMap.futureText3")}</Text>
          </Text>
          <br />
          <Flex gap="small">
            <Flex.Item>
              <Button
                color="primary"
                renderIcon={IconDownloadLine}
                onClick={handleFuture}
                interaction={
                  !!futureMap && !!futureMap.term ? "enabled" : "disabled"
                }
              >
                {t("manageMap.futureMap")}
              </Button>
            </Flex.Item>
          </Flex>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button margin="0 small 0 0" onClick={handleCancel}>
          {t("global.cancel")}
        </Button>
        <Button
          color="primary"
          onClick={handleSubmit}
          disabled={!mapFile || uploading}
        >
          {uploading ? (
            <Spinner renderTitle={t("global.pleaseWait")} size="x-small" />
          ) : (
            t("global.submit")
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ManageMap;
